<template>
  <div
    :class="getDropDownContainerClassName"
  >
    <button
      v-if="useButtonToActivate"
      v-on-clickaway="closeDropDown"
      class="btnDefault"
      :disabled="disabled"
      @click="toggleShowList"
    >
      {{ menuTitle }}
      <i :class="[ $style.dropDownMenuCaret, 'jc-caret-down' ]" />
    </button>
    <a
      v-if="!useButtonToActivate"
      v-on-clickaway="handleClickaway"
      :class="getDropDownMenuClassName"
      href="javascript:void(0);"
      @click.prevent="toggleShowList"
    >
      <slot name="title" />
      <span
        v-if="locationLabel"
        :class="$style.locationLabel"
        :title="locationLabel"
      >
        {{ locationLabel }}
      </span>
      <span
        v-if="locationLabel"
        :class="$style.pipeSeparator"
      >
        |
      </span>
      <span
        :class="getDropDownMenuTitleClassName"
      >{{ menuTitle }}</span>
      <i :class="[ $style.dropDownMenuCaret, 'jc-caret-down' ]" />
    </a>
    <DropDownList
      :bottomAlign="bottomAlign"
      :classNames="classNames"
      :closeDropDown="closeDropDown"
      :displayMenuListOnTop="displayMenuListOnTop"
      :isLargeDropDown="false"
      :itemSection="itemSection"
      :leftAlignList="leftAlignList"
      :menuFontSize="menuFontSize"
      :menuSections="menuSections"
      :menuTitle="menuTitle"
      :onItemClick="onItemClickWithClose"
      :refresh="refresh"
      :showList="showList"
      :showMenuLabel="showMenuLabel"
      :storeModule="storeModule"
      v-on="$listeners"
    />
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import DropDownList from 'client/components/DropDownMenu/DropDownList.vue';
import DropDownMixin from 'client/components/DropDownMenu/DropDownMixin';

export default {
  name: 'DropDownMenu',

  components: {
    DropDownList,
  },

  mixins: [clickaway, DropDownMixin],

  props: {
    closeOnItemClick: {
      default: false,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    handleClickaway: {
      default: function show() {
        this.showList = false;
      },
      type: Function,
    },
    isFilterDropDown: {
      default: false,
      type: Boolean,
    },
    itemSection: {
      default: '',
      type: String,
    },
    locationLabel: {
      default: '',
      type: String,
    },
    menuTitle: {
      required: true,
      type: String,
    },
    onItemClick: {
      type: Function,
      default: () => {},
    },
    useButtonToActivate: {
      default: false,
      required: false,
      type: Boolean,
    },
  },

  data: () => ({
    showList: false,
  }),

  computed: {
    getDropDownContainerClassName() {
      const classNames = [];
      if (this.classNames && this.classNames.container) {
        classNames.push(this.$style[this.classNames.container]);
      }
      if (this.useButtonToActivate) {
        classNames.push(this.$style.dropDownButtonStyleContainer);
      } else {
        classNames.push(this.$style.dropDownContainer);
      }
      return classNames;
    },

    getDropDownMenuClassName() {
      const classNames = [this.$style.dropDownMenu];
      if (this.disabled) {
        classNames.push(this.$style.dropDownMenuDisabled);
      }
      if (this.locationLabel) {
        classNames.push(this.$style.dropDownMenuWithLocation);
      }
      if (this.isFilterDropDown) {
        classNames.push(this.$style.filterDropDownMenu);
      }
      return classNames;
    },

    getDropDownMenuTitleClassName() {
      const { dropDownMenuTitle } = this.classNames;
      return [
        this.locationLabel ? 'hidden-xs' : '',
        this.$style[dropDownMenuTitle] || '',
      ];
    },
  },

  methods: {
    closeDropDown() {
      this.showList = false;
    },

    onItemClickWithClose(...props) {
      this.onItemClick(...props);
      if (this.closeOnItemClick) {
        this.closeDropDown();
      }
    },

    toggleShowList() {
      this.$emit('open', this.menuTitle);
      if (this.disabled) return;
      this.showList = !this.showList;
    },
  },
};
</script>

<style scoped module>
@value ( max-width-medium, max-width-medium-large ) from '~css/breakpoints.css';
@value (
  gradientDark2,
  gradientDark4,
  gradientLight4
) from '~css/colors.css';
@value ( buttonTransition ) from '~css/transitions.css';

.dropDownContainer {
  align-items: center;
  display: flex;
  font-weight: 300;
  justify-content: flex-end;
  margin-right: 25px;
  position: relative;
}

.dropDownContainerNoFlexEnd {
  display: flex;
  justify-content: unset;
  position: relative;
}

.dropDownButtonStyleContainer {
  display: inline-block;
  font-size: 13px;
  position: relative;
}

.dropDownMenu {
  background-color: transparent;
  color: var(--jc-text-color-dark);
  outline: none !important;
  text-decoration: none !important;
  white-space: nowrap;
}

.dropDownMenuDisabled {
  color: gradientDark4 !important;
  cursor: default;
}

.dropDownMenu > * {
  display: inline-block;
  vertical-align: middle;
}

.dropDownMenuCaret {
  color: inherit;
  line-height: 9px;
  padding-left: 4px;
  position: relative;
  top: 1px;
}

.dropDownMenuEnter,
.dropDownMenuLeaveTo {
  opacity: 0;
}

.dropDownMenuTransition {
  transition: dropDownMenuTransition;
}

.dropDownMenuWithLocation {
  align-items: center;
  display: flex;
  gap: 4px;
}

.filterBarContainer {
  background-color: gradientLight4;
  margin-left: -2px;
  margin-right: 0;
}

.filterDropDownMenu {
  background-color: gradientLight4;
  border: none;
  border-radius: 2px;
  color: gradientDark2 !important;
  font-size: 1rem;
  font-weight: 600;
  height: 100%;
  line-height: 2;
  outline: 0;
  padding: 5px 20px;
  transition: buttonTransition;
}

.locationLabel {
  font-weight: 700;
  max-width: 500px;
  overflow: hidden;
  position: relative;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pipeSeparator {
  font-size: 32px;
  font-weight: 100;
  padding: 0 0 6px 0;
}

.detailsAsideStatusMenuTitle {
  color: gradientDark2;
  font-size: 1.1rem;
  font-weight: 600;
  margin-right: 2px;
  vertical-align: middle;
}

.changeStateMenuTitle {
  color: var(--jc-color-informational-base);
}

@media (max-width: max-width-medium-large) {
  .locationLabel {
    max-width: 300px;
  }
}

@media (max-width: max-width-medium) {
  .pipeSeparator {
    content: '';
  }
}
</style>
